import moment from "moment";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";
import { usePermission } from "helpers/globalContext/usePermission";
import { useRequiredContext } from "helpers/globalContext/useRequiredContext";
import { defaultEntityLinkRouterHandler } from "helpers/routing/EntityLinkRouter/EntityLinkRouter.api.handler";

import { type DiscussionsLinkedTypes } from "types/enum";

import { getLocalBlobUrlFromExternalResource } from "utilities/file/file";
import { getCurrentPortalType } from "utilities/portal/portal";
import { retryPolicy } from "utilities/retryPolicy/retryPolicy";

import {
    type EditorImageDownloadHandler,
    type EditorImageUploadHandler,
} from "commonComponents/btWrappers/editor/core/extensions/custom/image/ImageExtension";
import { type UserMentionSuggestion } from "commonComponents/btWrappers/editor/core/extensions/custom/mentionUser/MentionUserExtension";
import {
    type IChatWithEntityLinkFeatureSetConfig,
    type IChatWithImageFeatureSetConfig,
} from "commonComponents/btWrappers/editor/core/extensions/ExtensionProvider.types";

import { defaultChatHandler, type IChatHandler } from "entity/chat/Chat.api.handler";
import { type IChatWebviewInitializationSettings } from "entity/chat/common/chat.types";

export interface IUseChatEditorConfigOptions {
    handler?: IChatHandler;
    webviewInitializationSettings?: IChatWebviewInitializationSettings;
    jobId?: number;
    searchUsers?: (query: string) => Promise<UserMentionSuggestion[]>;
    // When set, keeps the mention user extension enabled but shows an unsupported entity message
    // Defaults to undefined
    unsupportedMentionUserEntityType?: DiscussionsLinkedTypes;
    disableMentionUsers?: boolean; // Defaults to false. When true, disables the mention user feature entirely
}
const emptyObj = {};
export function useChatEditorConfig({
    handler = defaultChatHandler,
    webviewInitializationSettings = undefined,
    jobId,
    searchUsers,
    unsupportedMentionUserEntityType = undefined,
    disableMentionUsers = false,
}: IUseChatEditorConfigOptions = emptyObj): IChatWithImageFeatureSetConfig | undefined {
    const builderInfo = useRequiredContext(BuilderInfoContext);
    const handleImageUpload: EditorImageUploadHandler = useCallback(
        async (file, onProgress) => {
            const uploadData = await handler.uploadMedia(
                builderInfo.builderId,
                file,
                getCurrentPortalType(),
                onProgress,
            );

            return {
                downloadUrl: `${window.location.origin}/apix/v2.0/Channels/Media/${uploadData.mediaId}`,
                uploadMetadata: uploadData.mediaId,
            };
        },
        [handler, builderInfo.builderId],
    );
    // This garbage is because we need to use static references for the config
    const handleImageUploadStaticRef = useRef(handleImageUpload);
    useEffect(() => {
        handleImageUploadStaticRef.current = handleImageUpload;
    }, [handleImageUpload]);

    const handleImageDownload: EditorImageDownloadHandler = useCallback(
        async (_, metadata, onProgress, abortSignal) => {
            return {
                blobUrl: await retryPolicy(
                    async () => {
                        return await getLocalBlobUrlFromExternalResource(
                            `${window.location.origin}/apix/v2.0/Channels/Media/${metadata}`,
                            {
                                onProgressUpdate: onProgress,
                                headers: {
                                    PortalType: String(getCurrentPortalType()),
                                },
                            },
                        );
                    },
                    {
                        attemptTimings: [
                            moment.duration(1, "second"),
                            moment.duration(8, "seconds"),
                        ],
                        abortSignal,
                    },
                ),
            };
        },
        [],
    );
    // This garbage is because we need to use static references for the config
    const handleImageDownloadStaticRef = useRef(handleImageDownload);
    useEffect(() => {
        handleImageDownloadStaticRef.current = handleImageDownload;
    }, [handleImageDownload]);

    const canViewPhotos = usePermission("photos", "canView");

    const searchUsersStaticRef = useRef(searchUsers);
    useEffect(() => {
        searchUsersStaticRef.current = searchUsers;
    }, [searchUsers]);

    return useMemo<IChatWithImageFeatureSetConfig | undefined>(() => {
        const imageConfig: IChatWithImageFeatureSetConfig = {
            image: {
                configure: {
                    allowBase64: false,
                    uploadConfig: {
                        onUploadStaticRef: async (file, onProgress) =>
                            await handleImageUploadStaticRef.current(file, onProgress),
                        onDownloadStaticRef: async (url, metadata, onProgress, abortSignal) =>
                            await handleImageDownloadStaticRef.current(
                                url,
                                metadata,
                                onProgress,
                                abortSignal,
                            ),
                        isImageUploadDisabledByBrowser:
                            webviewInitializationSettings !== undefined &&
                            !webviewInitializationSettings.isImageUploadSupportedByNative,
                    },
                    browseBuildertrendFilesConfig: jobId
                        ? {
                              jobId,
                              canViewPhotosPermission: canViewPhotos,
                          }
                        : undefined,
                },
            },
        };

        const entityLinkConfig: IChatWithEntityLinkFeatureSetConfig = {
            entityLink: {
                configure: {
                    handler: defaultEntityLinkRouterHandler,
                },
            },
        };
        const mentionConfig = !disableMentionUsers
            ? {
                  mentionUser: {
                      configure: {
                          searchUsersStaticRef: searchUsersStaticRef.current,
                          unsupportedEntityType: unsupportedMentionUserEntityType,
                      },
                  },
              }
            : undefined;

        return {
            ...imageConfig,
            ...entityLinkConfig,
            ...mentionConfig,
        };
    }, [
        canViewPhotos,
        disableMentionUsers,
        jobId,
        unsupportedMentionUserEntityType,
        webviewInitializationSettings,
    ]);
}
